import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import PartnerInfo from "../../components/partner-info";
import { AccountBalanceOutlined } from "@mui/icons-material";

export type Option = {
  label: string;
  value: string;
  id?: number | string;
  mobile?: string;
  type?: string;
  profilePic?: string;
  address?: string;
};

type Props = {
  options: Option[];
  selected: Option[];
  onChange: any;
  placeholder?: string;
  fromMainScreen?: boolean;
  fromDeleteMarketScreen?: boolean;
  hideBankIcon?: boolean;
  className?: string;
};

export default function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = "Search...",
  fromMainScreen = false,
  fromDeleteMarketScreen = false,
  hideBankIcon = false,
  className,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);
  // n outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setOpen(false);
        setSearch("");
      }
    };

    window.addEventListener("click", handleClickOutside, true);
  }, []);

  // Filter options
  const normalizeText = (str: string = "") =>
    str?.toString().toLowerCase().replace(/\s+/g, "") ?? "";

  const filtered = Array.isArray(options)
    ? options.filter((opt) => {
        const label = normalizeText(opt?.label).includes(normalizeText(search));
        const id = opt?.id?.toString().includes(search.trim());
        return label || id;
      })
    : [];

  const toggleSelect = (option: Option) => {
    if (selected.some((s) => s.value === option?.value)) {
      onChange(selected.filter((s) => s.value !== option?.value));
    } else {
      onChange([...selected, option]);
    }
  };

  const removeSelected = (value: string) => {
    onChange(selected.filter((s) => s.value !== value));
  };

  const toggleSelectAll = () => {
    if (selected.length === filtered.length) {
      onChange([]);
    } else {
      onChange(filtered);
    }
  };
  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 240; // approx max-height of dropdown
      setDropUp(spaceBelow < dropdownHeight);
    }
  }, [open]);
  return (
    <div ref={dropdownRef} className={`relative w-full `}>
      <div
        className={`border rounded-md flex items-center px-2 py-3 cursor-pointer bg-white gap-2 ${className}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {!open && (
          <div className="flex flex-wrap gap-1 flex-1 text-sm">
            {selected.length > 0 ? (
              selected.slice(0, 2).map((s) => (
                <span
                  key={s.value}
                  className="bg-[#D7F3DD] rounded-full px-2 py-1 flex items-center gap-1"
                >
                  {s.label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelected(s.value);
                    }}
                    className="color-red"
                  >
                    ✕
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
            {selected.length > 2 && (
              <span className="bg-[#D7F3DD] rounded-full px-2 py-1">
                +{selected.length - 2}
              </span>
            )}
          </div>
        )}
        {open && (
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!open) setOpen(true);
            }}
            placeholder={placeholder}
            className="flex-1 outline-none cursor-text text-sm"
          />
        )}
        <FaChevronDown
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          size={10}
          color="#16a34a"
        />
      </div>

      {open && (
        <div
          className={`${"absolute w-full"} z-10 bg-white border rounded-md   shadow-lg ${
            dropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {filtered.length > 0 && (
            <div
              className="px-3 py-2 flex items-center gap-2 cursor-pointer text-sm hover:bg-green-50 border-b"
              onClick={(e) => {
                e.stopPropagation();
                toggleSelectAll();
              }}
            >
              <input
                type="checkbox"
                checked={
                  selected.length === options.length && options.length > 0
                }
                readOnly
              />
              Select All
            </div>
          )}

          <div className="max-h-60 overflow-auto text-sm">
            {filtered.length > 0 ? (
              filtered?.map((option) => (
                <div
                  // key={option.id}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#D7F3DD] border-b ${
                    selected.some((s) => s.value === option.value)
                      ? "bg-[#D7F3DD]"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(option);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.some((s) => s.value === option.value)}
                    readOnly
                  />
                  {fromMainScreen ? (
                    <div className="flex items-center gap-2 fs-14 color-text-primary font-semibold">
                      {!hideBankIcon && (
                        <AccountBalanceOutlined
                          style={{ fontSize: 22, color: "color-sub-text" }}
                        />
                      )}
                      <span>{option?.label}</span>
                    </div>
                  ) : (
                    <PartnerInfo
                      profilePic={option?.profilePic || ""}
                      name={option?.label}
                      shortName={""}
                      partnerType={option?.type}
                      partnerId={option?.id}
                      partnerMobile={option?.mobile || ""}
                      fromLoansParty={true}
                      partyName={option?.address}
                    />
                  )}

                  {/* {option.label} */}
                </div>
              ))
            ) : (
              <div className="px-3 py-4  text-sm text-center">
                No data found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
